import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { type FacilityType } from '@his/shared';
import { get } from '@/lib/api';
import { DataTable, PageHeader } from '@/components/ui/layout';
import { Alert, Badge } from '@/components/ui/primitives';
import { useOwnerCapability } from './owner-nav';

export interface Plan {
  id: string;
  code: string;
  name: string;
  description: string | null;
  trialDays: number;
  moduleFlags: string[];
  facilityType: FacilityType;
  isActive: boolean;
  tenantCount: number;
}

export const FACILITY_LABELS: Record<string, string> = {
  hospital: 'Hospital',
  lab: 'Laboratory',
  pharmacy: 'Medical store',
};

export function PlansPage() {
  const navigate = useNavigate();
  const canEdit = useOwnerCapability('plan.update');

  const plans = useQuery({
    queryKey: ['plans', 'all'],
    queryFn: () => get<Plan[]>('/platform/plans'),
  });

  return (
    <>
      <PageHeader
        title="Plans"
        description="Four packages: OPD, IPD, Laboratory and Pharmacy. A facility takes any combination of them; what each facility pays is set on the facility itself."
      />

      <Alert className="mb-4">
        Each package carries a fixed set of modules, so there are four packages and no way to add a fifth. A facility takes any combination of them; when the owner adds or removes one, the modules follow, including on a facility that runs on its own server at its next check-in.
        Price, billing cycle and usage limits belong to the facility — set them when you provision it, or on its
        own page afterwards.
      </Alert>

      <DataTable
        rows={plans.data}
        loading={plans.isLoading} refreshing={plans.isFetching}
        error={plans.error}
        rowKey={(row) => row.id}
        onRowClick={(row) => {
          if (canEdit) navigate(`/owner/plans/${row.id}/edit`);
        }}
        empty="The four packages are seeded on install. Run the seed if this list is empty."
        columns={[
          {
            header: 'Plan',
            cell: (row) => (
              <div>
                <p className="font-medium">{row.name}</p>
                <p className="text-xs text-muted-foreground">{row.description ?? row.code}</p>
              </div>
            ),
          },
          {
            header: 'Facility type',
            cell: (row) => <Badge variant="secondary">{FACILITY_LABELS[row.facilityType]}</Badge>,
          },
          { header: 'Trial', align: 'right', cell: (row) => `${row.trialDays} d` },
          { header: 'Modules', align: 'right', cell: (row) => row.moduleFlags.length },
          { header: 'Facilities', align: 'right', cell: (row) => row.tenantCount },
        ]}
      />

    </>
  );
}
